import React from 'react';
import Button from '../../../ui/Button';
import Spinner from '../../../ui/Spinner';
import { ReactComponent as Download } from '../../../../assets/icons/download.svg';

import * as Styles from './CancellationStyles';
import PrescriptionInterface from '../../../../interfaces/prescription';
import { trackClickDownloadFullPrescription, trackClickValidateSignature }
  from '../../../../monitoring/interaction/prescription-detail';
import { useTrackingProperties } from '../../../../monitoring/utils';
import { usePrescriptionPdfDownload } from '../../../../hooks/usePrescriptionPdfDownload';

interface PrescriptionActionsBarProps {
  prescription: PrescriptionInterface;
  showFullPrescriptionDownloadButton: boolean;
}

const FULL_PDF_LOADING_KEY = 'full';

function PrescriptionActionsBar({
  prescription,
  showFullPrescriptionDownloadButton,
}: PrescriptionActionsBarProps): JSX.Element {
  const { downloadFullPdf, isLoading } = usePrescriptionPdfDownload(prescription.token);
  const isPrescriptionSigned = prescription.signed;
  const { drugstoreId, chainId, drugstoreName, chainName, drugstoreUF } = useTrackingProperties();
  const isDownloading = isLoading(FULL_PDF_LOADING_KEY);

  const isPdfAvailable = (prescriptionParam: PrescriptionInterface): boolean =>
    Boolean(prescriptionParam.document.pdf_access);

  const shouldDisablePrintAllAndDownload = (prescriptionParam: PrescriptionInterface): boolean => {
    const canDownload = showFullPrescriptionDownloadButton;
    return !canDownload || !isPdfAvailable(prescriptionParam) || isDownloading;
  };

  const getTitleDownloadPDF = (prescriptionParam: PrescriptionInterface): string => {
    const hasItemDispensed = prescriptionParam.items.some((item) => item.dispensed);

    if (!hasItemDispensed) {
      return 'É necessário dispensar algum medicamento para ter acesso ao PDF';
    }

    if (isPdfAvailable(prescriptionParam)) {
      return 'Clique para realizar o download do PDF da receita completa';
    }

    return 'O PDF da receita não está disponível para download';
  };

  const getTitleValidateSignature = (prescriptionParam: PrescriptionInterface): string => {
    const hasItemDispensed = prescriptionParam.items.some((item) => item.dispensed);

    if (!hasItemDispensed) {
      return 'É necessário dispensar algum medicamento para ter acesso ao validador de assinatura';
    }

    if (!prescriptionParam.signed) {
      return 'A prescrição precisa estar assinada para validar a assinatura';
    }

    return 'Clique para acessar o validador de assinatura';
  };

  const handleDownloadFullPdf = async (): Promise<void> => {
    if (shouldDisablePrintAllAndDownload(prescription)) {
      return;
    }

    trackClickDownloadFullPrescription({
      prescriptionId: prescription.id,
      drugstoreId,
      chainId,
      drugstoreName,
      chainName,
      drugstoreUF,
    });

    await downloadFullPdf({
      loadingKey: FULL_PDF_LOADING_KEY,
      getPdfAccess: () => prescription.document.pdf_access,
      getRefreshedPdfAccess: (refreshedPrescription) => refreshedPrescription.document.pdf_access,
      errorTitle: 'Erro ao baixar prescrição completa',
    });
  };

  return (
    <Styles.ActionsBar>
      <Button
        as='a'
        target='_blank'
        aria-disabled={shouldDisablePrintAllAndDownload(prescription)}
        className={!isPrescriptionSigned ? ' disabled-link' : ''}
        disabled={!isPrescriptionSigned}
        href={!isPrescriptionSigned ? `/#/` : 'https://validador.memed.com.br/'}
        onClick={(e: React.MouseEvent) => {
          trackClickValidateSignature({
            prescriptionId: prescription.id,
            drugstoreId,
            chainId,
            drugstoreName,
            chainName,
            drugstoreUF,
          });
          if (!isPrescriptionSigned) {
            e.preventDefault();
          }
        }}
        title={getTitleValidateSignature(prescription)}
      >
        Validar assinatura
      </Button>
      <Button
        aria-disabled={shouldDisablePrintAllAndDownload(prescription)}
        className={[
          'tracking-download-pdf-dispensation',
          shouldDisablePrintAllAndDownload(prescription) ? 'disabled-link' : '',
        ].join(' ').trim()}
        disabled={shouldDisablePrintAllAndDownload(prescription)}
        onClick={handleDownloadFullPdf}
        title={getTitleDownloadPDF(prescription)}
      >
        <Styles.ButtonContent>
          {isDownloading ? (
            <Spinner size='16px' />
          ) : (
            <Download style={{ width: '16px', height: '16px' }} />
          )}
          Baixar prescrição completa
        </Styles.ButtonContent>
      </Button>
    </Styles.ActionsBar>
  );
}

export default PrescriptionActionsBar;
